/**
 * Year filter for leave lists (My Leaves, HR Applied Leaves).
 *
 * A leave belongs to the year its START date falls in — a 30 Dec → 2 Jan leave
 * is a previous-year leave. The selectable range runs from whichever is later,
 * 2025 (the first year of leave data) or the last five years including the
 * current one, up to the current year.
 */

/** First year with leave data in the system. */
export const LEAVE_YEAR_FLOOR = 2025
/** How many years (including the current one) the filter reaches back. */
export const LEAVE_YEAR_SPAN = 5

export interface LeaveYearBounds {
  min: number
  max: number
}

export function leaveYearBounds(today: Date = new Date()): LeaveYearBounds {
  const max = today.getFullYear()
  return { min: Math.max(LEAVE_YEAR_FLOOR, max - (LEAVE_YEAR_SPAN - 1)), max }
}

/** Parse a `?year=` value; anything missing, malformed or out of range falls
 *  back to the current year. */
export function resolveLeaveYear(raw: string | undefined, today: Date = new Date()): number {
  const { min, max } = leaveYearBounds(today)
  if (!raw || !/^\d{4}$/.test(raw)) return max
  const year = Number(raw)
  return year >= min && year <= max ? year : max
}

/** Calendar year of an ISO `YYYY-MM-DD` start date. */
export function leaveStartYear(startDate: string): number {
  return Number(startDate.slice(0, 4))
}

// ── URL search helpers (HR Applied Leaves) ───────────────────────────────────
// The year control and the Leave Dates range are one control surface: a range
// in another year moves the year to it, and changing the year clears the range.
// These return a NEW search object; the caller navigates once.

type SearchParams = Readonly<Record<string, string>>
/** `?year=` for `year` — dropped when it is the current (default) year. */
function putYear(search: Record<string, string>, year: number, bounds: LeaveYearBounds): void {
  if (year === bounds.max) delete search.year
  else search.year = String(year)
}

/** Switch the year control; clears the Leave Dates range (it belonged to the
 *  old year). */
export function searchWithYear(
  search: SearchParams,
  year: number,
  bounds: LeaveYearBounds,
): Record<string, string> {
  const { since: _since, until: _until, ...rest } = search
  const next: Record<string, string> = { ...rest }
  putYear(next, year, bounds)
  return next
}

/** Set the Leave Dates range (`since`/`until`) in one go. The year control
 *  follows the range's START date; a range running into the next year is cut
 *  at 31 Dec of the start year, since the list shows one year at a time.
 *  `null`s clear the range. */
export function searchWithDateRange(
  search: SearchParams,
  since: string | null,
  until: string | null,
  currentYear: number,
  bounds: LeaveYearBounds,
): Record<string, string> {
  const { since: _since, until: _until, ...rest } = search
  const next: Record<string, string> = { ...rest }
  if (!since) return next
  const year = leaveStartYear(since)
  if (year !== currentYear) putYear(next, year, bounds)
  const yearEnd = `${year}-12-31`
  next.since = since
  next.until = until && until <= yearEnd ? until : until ? yearEnd : since
  return next
}
